/**
 * All possible environments of the application.
 */
export const Environments = {
	Local: 'local',
	Develop: 'dev',
	Iom: 'iom',
	Test: 'test',
	Acceptance: 'acceptance',
	Production: 'production',
	Backend: 'backend'
} as const;

/**
 * All values of the Environments object. This can be used to check the value
 * of the current the environment.
 */
export type EnvironmentType = (typeof Environments)[keyof typeof Environments];

/**
 * The available languages in the application. If a language is not in this list,
 * it cannot be configured in the `environment.[ENV].ts` file, and will therefore
 * not be available in the application.
 */
export const AvailableLanguages = ['nl', 'en', 'fr'] as const;

export interface EnvironmentEntity {
	environment: EnvironmentType;
	version: string;
	production: boolean;
	domain: string;
	cookieDomain: string;
	metaDomain: string;
	/**
	 * The location and settings for the Drupal Headless CMS.
	 */
	api: ApiEnvEntity;
	assets: AssetsEnvEntity;
	acmidm: AcmIdmEnvEntity;
	sas: SasEnvEntity;
	bpost: BpostEnvEntity;
	ebox: EboxEnvEntity;
	matomo: MatomoEnvEntity;
	modules: ModulesEnvEntity;
	cacheBust: string;
	languages: (typeof AvailableLanguages)[number][];
	/**
	 * The service worker is used to cache the application and make it available offline.
	 *
	 * @deprecated The Service Worker is no longer in use. This variable may be removed.
	 */
	whatsNewPageId: string;
	/**
	 * ! WARNING !
	 * -
	 * Enable the identifiable tracking for the user.
	 * This will result in the user no longer being tracked by a hash, but by
	 * an identifiable combination of their name and organization number.
	 * **This is a MAJOR GDPR breach on production environments.**
	 */
	allowIdentifiableTacking: boolean;
	logErrors: boolean;
}

/**
 * The location and settings for the Drupal Headless CMS.
 */
interface ApiEnvEntity {
	protocol: string;
	hostname: string;
	apiPath: string;
	fullPath: string;
}

interface AssetsEnvEntity {
	fullPath: string;
	styleExtension: string;
}

interface AcmIdmEnvEntity {
	protocol: string;
	hostname: string;
	loginPath: string;
	logoutPath: string;
	switchPath: string;
	directedLoginPath: string;
	keepAliveUrl: string;
	widgetsUrl: string;
	headerWidgetId: string;
	footerWidgetId: string;
}

interface SasEnvEntity {
	enabled: boolean;
	url: string;
	sourceUuid: string;
	targetUuid: string;
}

interface BpostEnvEntity {
	host: string;
}

interface EboxEnvEntity {
	guiUrl: string;
	activationUrl: string;
	infoUrl: string;
}

interface MatomoEnvEntity {
	siteId: number;
}

interface ModulesEnvEntity {
	ebox: number;
	iom: number;
}
